import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, 
  Video, BarChart3, ClipboardList, Calendar, BookMarked,
  Gamepad2, FileText, PenTool, Languages, Settings,
  CheckCircle, XCircle, ExternalLink, Copy, Mail,
  AlertTriangle, Info, Loader2, RefreshCw, Globe, Headphones
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AdminSettings = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);
  const [tutoringStatus, setTutoringStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: ClipboardList },
    { path: '/admin/reports', label: 'Monthly Reports', icon: Calendar },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/curriculum-browser', label: 'Curriculum Browser', icon: BookMarked },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
  ];

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const [emailRes, tutoringRes] = await Promise.all([
        api.get('/v2/admin/email-status'),
        api.get('/tutoring/sessions/available')
      ]);
      setEmailStatus(emailRes.data);
      setTutoringStatus({
        configured: true, // API is working
        demo_mode: true // No DAILY_API_KEY in production
      });
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setTestingEmail(true);
    try {
      const res = await api.post(`/v2/admin/test-email?test_email=${encodeURIComponent(testEmail)}`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <ProLayout title="Admin Settings" sidebarItems={sidebarItems}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout title="Admin Settings" sidebarItems={sidebarItems}>
      <div className="max-w-4xl" data-testid="admin-settings">
        <h1 className="font-heading text-2xl font-bold text-slate-800 mb-6">Platform Settings</h1>

        {/* Email Service Configuration */}
        <div className="pro-card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800">Email Service (Resend)</h2>
              <p className="text-sm text-slate-500">Send notifications to parents and students</p>
            </div>
            {emailStatus?.email_configured ? (
              <span className="ml-auto flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" /> Configured
              </span>
            ) : (
              <span className="ml-auto flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                <XCircle className="w-4 h-4" /> Not Configured
              </span>
            )}
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Status:</span>
                <span className="ml-2 font-medium text-slate-800">{emailStatus?.status || 'Unknown'}</span>
              </div>
              <div>
                <span className="text-slate-500">Sender Email:</span>
                <span className="ml-2 font-medium text-slate-800">{emailStatus?.sender_email || 'Not set'}</span>
              </div>
            </div>
          </div>

          {emailStatus?.sender_email === 'onboarding@resend.dev' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Using Resend Test Domain</p>
                  <p className="text-sm text-amber-700 mt-1">
                    You're using the default <code className="bg-amber-100 px-1 rounded">onboarding@resend.dev</code> sender. 
                    This only allows sending to your Resend account email.
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    To send emails to any recipient, verify your domain at{' '}
                    <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                      resend.com/domains
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Test Email */}
          <div className="border-t pt-4">
            <p className="font-medium text-slate-700 mb-2">Send Test Email</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                data-testid="test-email-input"
              />
              <Button 
                onClick={sendTestEmail} 
                disabled={testingEmail}
                data-testid="send-test-email-btn"
              >
                {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Test
              </Button>
            </div>
          </div>
        </div>

        {/* Daily.co Live Tutoring Configuration */}
        <div className="pro-card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800">Live Tutoring (Daily.co)</h2>
              <p className="text-sm text-slate-500">Video sessions for 1:1 and group tutoring</p>
            </div>
            <span className="ml-auto flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              <AlertTriangle className="w-4 h-4" /> Demo Mode
            </span>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Running in Demo Mode</p>
                <p className="text-sm text-amber-700 mt-1">
                  Live tutoring is currently in demo mode. Sessions can be created and booked, 
                  but video rooms won't work until you configure a Daily.co API key.
                </p>
              </div>
            </div>
          </div>

          {/* Setup Guide */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 border-b">
              <h3 className="font-semibold text-slate-800">Setup Guide</h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                <div>
                  <p className="font-medium text-slate-800">Create a Daily.co Account</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Sign up for a free account at{' '}
                    <a href="https://www.daily.co" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">
                      daily.co
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                <div>
                  <p className="font-medium text-slate-800">Get Your API Key</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Go to{' '}
                    <a href="https://dashboard.daily.co/developers" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">
                      Dashboard → Developers
                    </a>{' '}
                    and copy your API key
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                <div>
                  <p className="font-medium text-slate-800">Add to Environment</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Add the following to your backend <code className="bg-slate-100 px-1 rounded">.env</code> file:
                  </p>
                  <div className="mt-2 bg-slate-900 text-green-400 rounded-lg p-3 font-mono text-sm flex items-center justify-between">
                    <code>DAILY_API_KEY=your_api_key_here</code>
                    <button 
                      onClick={() => copyToClipboard('DAILY_API_KEY=your_api_key_here')}
                      className="text-slate-400 hover:text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                <div>
                  <p className="font-medium text-slate-800">Restart Backend</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Restart the backend server to apply the new configuration
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Info */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Daily.co Pricing</p>
                <p className="text-sm text-blue-700 mt-1">
                  Daily.co offers a generous free tier with 2,000 participant minutes/month. 
                  Perfect for getting started with live tutoring!
                </p>
                <a 
                  href="https://www.daily.co/pricing" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium mt-2 hover:underline"
                >
                  View Pricing <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Lesson Generation */}
        <div className="pro-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800">Automated Daily Lessons</h2>
              <p className="text-sm text-slate-500">Personalized lessons generated weekly</p>
            </div>
            <span className="ml-auto flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" /> Active
            </span>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Schedule:</span>
                <span className="ml-2 font-medium text-slate-800">Every Sunday at Midnight (UTC)</span>
              </div>
              <div>
                <span className="text-slate-500">Lessons per Student:</span>
                <span className="ml-2 font-medium text-slate-800">2 personalised lessons</span>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm text-slate-600 mb-3">
              Lessons are automatically generated based on student performance, weak areas, 
              and spaced repetition algorithms.
            </p>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const res = await api.post('/adaptive/generate-weekly-lessons');
                  toast.success(`Generated lessons for ${res.data.results.successful} students!`);
                } catch (error) {
                  toast.error('Failed to generate lessons');
                }
              }}
              data-testid="generate-lessons-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Manually Trigger Generation
            </Button>
          </div>
        </div>

        {/* ESOL Interactive Lessons */}
        <div className="pro-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h2 className="font-bold text-slate-800">ESOL Interactive Lessons</h2>
                <p className="text-sm text-slate-500">26 lessons with vocabulary, exercises & audio</p>
              </div>
            </div>
            <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" /> Active
            </span>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Total Topics:</span>
                <span className="ml-2 font-medium text-slate-800">26 ESOL lessons</span>
              </div>
              <div>
                <span className="text-slate-500">CEFR Levels:</span>
                <span className="ml-2 font-medium text-slate-800">A1, A2, B1, B2</span>
              </div>
              <div>
                <span className="text-slate-500">Per Lesson:</span>
                <span className="ml-2 font-medium text-slate-800">10 words, 5 phrases, 5 exercises</span>
              </div>
              <div>
                <span className="text-slate-500">Features:</span>
                <span className="ml-2 font-medium text-slate-800">TTS Audio, Instant Feedback</span>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex gap-3">
              <Button 
                onClick={() => navigate('/admin/preview/esol')}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="preview-esol-btn"
              >
                <Headphones className="w-4 h-4 mr-2" />
                Preview ESOL Lessons
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Each lesson includes vocabulary with phonetics, useful phrases, interactive exercises, and text-to-speech audio for pronunciation practice.
            </p>
          </div>
        </div>
      </div>
    </ProLayout>
  );
};

export default AdminSettings;
